import {
  type BiomeId,
  type BuyRejected,
  type BuyResult,
  type ClassId,
  getItem,
  type InventoryState,
  type SellRejected,
  type SellResult,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { benchHead, purseChip, setPurse } from './bench.js';
import { closeButton } from './hud.js';
import { hideItemHover } from './item-hover.js';
import { kitTitle } from './kit.js';
import { MerchantBuyView } from './merchant-buy-view.js';
import { formatGold, type MerchantView } from './merchant-row.js';
import { MerchantSellView } from './merchant-sell-view.js';
import { uiFormat, uiNumber, uiText } from './text.js';
import './smith.css';
import './merchant.css';

/**
 * The merchant's window (ADR 0016), in two sides. To buy: the common pieces
 * of his biome and the supplies every merchant keeps, at his price, a count
 * chosen for the supplies; hovering one (tapping it on a phone) opens its
 * card, set against the piece worn in its slot. To sell: the bag's items he
 * would take, each with what it fetches, right here (the bag no longer has
 * to open beside it, which a phone had no room for). The purse sits by the
 * tabs. Like every panel it only sends intents.
 */

export type MerchantMode = 'buy' | 'sell';

export interface MerchantPanelOptions {
  icons: CssAtlas | null;
  /** The biome of the merchant being visited: his stock depends on it. */
  biome: () => BiomeId | undefined;
  /** The zone the merchant stands in: the tutorial's keeps a stock of his own. */
  zoneId: () => string | undefined;
  /** The player: a piece's card judges his level and class. */
  wearer: () => { level: number; classId: ClassId };
  onBuy: (templateId: string, quantity: number) => void;
  onSell: (itemId: string, quantity: number) => void;
}

const SELL_REFUSALS: Record<SellRejected['reason'], string> = {
  invalid: 'Demande invalide.',
  'unknown-item': 'Tu n’as plus cet objet dans ton sac.',
  worthless: 'Le marchand n’en veut pas.',
  quantity: 'Tu n’en as pas autant.',
  'too-far': 'Approche-toi du marchand.',
  kept: 'Garde-le, tu en as besoin pour les leçons du camp.',
};

const BUY_REFUSALS: Record<BuyRejected['reason'], string> = {
  invalid: 'Demande invalide.',
  'not-sold': 'Il ne vend pas ça ici.',
  'missing-gold': 'Ta bourse n’y suffit pas.',
  'bag-full': 'Ton sac est plein.',
  'too-far': 'Approche-toi du marchand.',
};

export class MerchantPanel {
  private readonly root: HTMLElement;
  private readonly tabs: Record<MerchantMode, HTMLButtonElement>;
  private readonly purse: HTMLElement;
  private readonly body: HTMLElement;
  private readonly message: HTMLElement;
  private readonly buyView: MerchantBuyView;
  private readonly sellView: MerchantSellView;
  private inventory: InventoryState | undefined;
  private inReach = false;
  private mode: MerchantMode = 'buy';
  /** Sales sent together (« Vendre tout le butin »): answered one by one, told once. */
  private batch: { left: number; count: number; gold: number } | undefined;
  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !this.root.hidden) this.close();
  };

  constructor(private readonly options: MerchantPanelOptions) {
    this.buyView = new MerchantBuyView(options);
    this.sellView = new MerchantSellView({
      onSell: options.onSell,
      onSellMany: (sales) => {
        if (sales.length === 0) return;
        this.batch = { left: sales.length, count: 0, gold: 0 };
        for (const sale of sales) options.onSell(sale.itemId, sale.quantity);
      },
    });

    this.root = document.createElement('section');
    this.root.className = 'smith merchant';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Marchand'));

    const title = document.createElement('h2');
    title.className = 'smith__title';
    kitTitle(title, 'merchant');
    title.textContent = uiText('Marchand');
    const close = closeButton('Fermer (Échap)', () => {
      this.close();
    });
    title.append(close);

    const tabBar = document.createElement('div');
    tabBar.className = 'merchant__tabs';
    tabBar.setAttribute('role', 'tablist');
    const tab = (mode: MerchantMode, label: string): HTMLButtonElement => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'merchant__tab';
      button.dataset.mode = mode;
      button.setAttribute('role', 'tab');
      button.textContent = label;
      button.addEventListener('click', () => {
        this.setMode(mode);
      });
      tabBar.append(button);
      return button;
    };
    this.tabs = { buy: tab('buy', uiText('Acheter')), sell: tab('sell', uiText('Vendre')) };
    this.purse = purseChip();
    const head = benchHead(tabBar, this.purse);

    this.body = document.createElement('div');
    this.body.className = 'bench__list';
    this.message = document.createElement('p');
    this.message.className = 'smith__message merchant__message';
    this.message.setAttribute('aria-live', 'polite');

    this.root.append(title, head, this.body, this.message);
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKey);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  /** Opens on the buying side: what he has is what one comes to see. */
  open(): void {
    this.root.hidden = false;
    this.say('', 'success');
    this.setMode('buy');
  }

  close(): void {
    if (this.root.hidden) return;
    this.root.hidden = true;
    this.batch = undefined;
    this.sellView.reset();
    hideItemHover();
  }

  setInventory(state: InventoryState): void {
    this.inventory = state;
    this.render();
  }

  /** Whether the player stands by the merchant; the buttons wait until they do. */
  setInReach(inReach: boolean): void {
    if (inReach === this.inReach) return;
    this.inReach = inReach;
    this.render();
  }

  showSold(result: SellResult): void {
    if (this.batch !== undefined) {
      this.batch.count += result.quantity;
      this.batch.gold += result.gold;
      this.settleBatch(true);
      return;
    }
    const name = uiText(getItem(result.templateId)?.name ?? result.templateId);
    const what = result.quantity > 1 ? `${String(result.quantity)} × ${name}` : name;
    this.say(
      uiFormat('Vendu : {what} pour {gold}.', { what, gold: formatGold(result.gold) }),
      'success',
    );
  }

  showBought(result: BuyResult): void {
    const name = uiText(getItem(result.templateId)?.name ?? result.templateId);
    const what = result.quantity > 1 ? `${String(result.quantity)} × ${name}` : name;
    this.say(
      uiFormat('Acheté : {what} pour {gold}.', { what, gold: formatGold(result.gold) }),
      'success',
    );
  }

  showSellRejected(rejection: SellRejected): void {
    // Mid-batch, or the batch just told what it sold: no refusal over it.
    if (this.batch !== undefined && !this.settleBatch(false)) return;
    this.say(uiText(SELL_REFUSALS[rejection.reason]), 'refused');
  }

  showBuyRejected(rejection: BuyRejected): void {
    this.say(uiText(BUY_REFUSALS[rejection.reason]), 'refused');
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKey);
    this.root.remove();
  }

  /**
   * One answer of a batch came back (`sold` or refused); the last one tells
   * the whole of it. Whether a refused sale still has to be told: only when
   * it ended a batch that sold nothing.
   */
  private settleBatch(sold: boolean): boolean {
    const batch = this.batch;
    if (batch === undefined) return true;
    batch.left -= 1;
    if (batch.left > 0) return false;
    this.batch = undefined;
    if (batch.count === 0) return !sold;
    this.say(
      uiFormat('Butin vendu : {count} objets pour {gold}.', {
        count: uiNumber(batch.count),
        gold: formatGold(batch.gold),
      }),
      'success',
    );
    return false;
  }

  private setMode(mode: MerchantMode): void {
    this.mode = mode;
    this.sellView.reset();
    this.root.dataset.mode = mode;
    for (const [key, button] of Object.entries(this.tabs)) {
      const active = key === mode;
      button.classList.toggle('merchant__tab--active', active);
      button.setAttribute('aria-selected', String(active));
    }
    this.body.scrollTop = 0;
    this.render();
  }

  private say(text: string, tone: 'success' | 'refused'): void {
    this.message.textContent = text;
    this.message.dataset.tone = tone;
  }

  private render(): void {
    if (this.root.hidden) return;
    setPurse(this.purse, this.inventory?.gold ?? 0);
    // The redraw takes the hovered row away without a mouseleave.
    hideItemHover();
    const scroll = this.body.scrollTop;
    this.body.replaceChildren();
    if (!this.inReach) {
      const far = document.createElement('p');
      far.className = 'smith__warn merchant__far';
      far.textContent = uiText('Approche-toi du marchand.');
      this.body.append(far);
    }
    const view: MerchantView = {
      icons: this.options.icons,
      inventory: this.inventory,
      inReach: this.inReach,
      wearer: this.options.wearer(),
      redraw: () => {
        this.render();
      },
    };
    if (this.mode === 'sell') this.sellView.render(this.body, view);
    else this.buyView.render(this.body, view);
    // A sale or a step redraws the list: it stays where the eye was.
    this.body.scrollTop = scroll;
  }
}
